import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useState, useEffect } from 'react';

interface PureIframeProps {
  url: string;
  maxRetries?: number;
  isFullScreen?: boolean;
}

const PureIframe: React.FC<PureIframeProps> = ({
  url,
  maxRetries = 2,
  isFullScreen = false,
}) => {
  const [loadingState, setLoadingState] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [error, setError] = useState(false);
  const [errorType, setErrorType] = useState<string | null>(null);
  const [iframeUrl, setIframeUrl] = useState(`${url}`);

  // eslint-disable-next-line no-console
  console.log('errorType', errorType, iframeUrl, error, loadingState);

  const handleIframeLoad = (event: React.SyntheticEvent<HTMLIFrameElement>) => {
    // eslint-disable-next-line no-console
    console.log('handleIframeLoad called!');

    let iframeDocument: Document | null = null;

    // Try to access iframe content with error handling for cross-origin
    try {
      iframeDocument =
        event.currentTarget.contentDocument ||
        event.currentTarget.contentWindow?.document ||
        null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Cross-origin access blocked:', error);
      // Cross-origin error - treat as JavaScript error to trigger retries
      setError(true);
      setErrorType('javascript_error');
      setLoadingState(true);
      return;
    }

    // eslint-disable-next-line no-console
    console.log('iframeDocument', iframeDocument);

    // Check if iframe contains error pages (only if we can access content)
    if (iframeDocument && iframeDocument.body) {
      const bodyText = iframeDocument.body.textContent || '';
      const bodyHTML = iframeDocument.body.innerHTML || '';

         
       // eslint-disable-next-line no-console
    console.log('iframeDocument', bodyHTML, bodyText);


      // Check for "You need to enable JavaScript" error (highest priority)
      if (bodyText.includes('You need to enable JavaScript to run this app')) {
        // eslint-disable-next-line no-console
        console.log('JavaScript error detected in iframe content');
        setError(true);
        setErrorType('javascript_error');
        setLoadingState(true);
        return;
      }
      
      // Check for 503 Service Temporarily Unavailable
      if (bodyText.includes('503 Service Temporarily Unavailable')) {
        // eslint-disable-next-line no-console
        console.log('503 error detected in iframe content');
        setError(true);
        setErrorType('503');
        setLoadingState(true);
        return;
      }

      // Check for other server errors
      if (
        bodyText.includes('502 Bad Gateway') ||
        bodyText.includes('404 Not Found') ||
        bodyText.includes('500 Internal Server Error') ||
        bodyText.includes('Service Unavailable') ||
        (bodyHTML.includes('<!doctype') && bodyText.includes('error')) ||
        (bodyHTML.includes('<html') && bodyText.includes('Error'))
      ) {
        // eslint-disable-next-line no-console
        console.log('Server error detected in iframe content');
        setError(true);
        setErrorType('server_error');
        setLoadingState(true);
        return;
      }
    }

    // If we reach here, the iframe loaded successfully
    setError(false);
    setErrorType(null);
    setLoadingState(false);
  };

  useEffect(() => {
    let retryInterval: NodeJS.Timeout | undefined;

    // Always retry until maxRetries is reached, regardless of error
    if (retryCount < maxRetries) {
      retryInterval = setTimeout(() => {
        setRetryCount(prev => prev + 1);
      }, 3000); // Retry every 3 seconds
    }

    return () => {
      if (retryInterval) clearTimeout(retryInterval);
    };
  }, [retryCount, maxRetries]);

  useEffect(() => {
    // Update URL when retrying
    if (retryCount > 0) {
      setIframeUrl(`${url}?retry=${retryCount}&timestamp=${Date.now()}`);
    } else {
      setIframeUrl(url);
    }
  }, [retryCount, url]);

  return (
    <div
      style={{ height: isFullScreen ? '100vh' : '80vh', position: 'relative' }}
    >
      {/* Debug console log */}
      {/* eslint-disable-next-line no-console */}
      {console.log('Render Debug:', {
        loadingState,
        retryCount,
        maxRetries,
        error,
        errorType,
      })}

      {/* Show loading spinner during retries */}
      {loadingState && retryCount > 0 && retryCount < maxRetries && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            textAlign: 'center',
          }}
        >
          <CircularProgress />
          <Typography variant="body1" sx={{ marginTop: 2 }}>
            {errorType === 'javascript_error'
              ? 'environment is being prepared...'
              : errorType === '503'
              ? 'Service is temporarily unavailable, retrying...'
              : 'Loading environment...'
            }
          </Typography>
          <Typography variant="caption" sx={{ marginTop: 1 }}>
            Loading... Attempt {retryCount}/{maxRetries}
          </Typography>
        </Box>
      )}

      {/* Show iframe after max retries for successful loads OR JavaScript errors */}
      {(!error || errorType === 'javascript_error') && (
        <iframe
          key="final-iframe"
          loading="eager"
          src={iframeUrl}
          width="100%"
          height="90%"
          onLoad={handleIframeLoad}
          style={{ display: 'block' }}
        ></iframe>
      )}

      {/* Show error message only for non-JavaScript errors after max retries */}
      {retryCount >= maxRetries && error && errorType !== 'javascript_error' && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: 'error.main',
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'error.main',
            borderRadius: '8px',
            padding: '16px',
            maxWidth: '600px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            zIndex: 1000,
          }}
        >
          <Typography
            variant="body1"
            sx={{ fontWeight: 'bold', marginBottom: 1, color: 'primary.main' }}
          >
            Failed to load the environment. Please Try Again
          </Typography>
          <Typography
            variant="body2"
            sx={{ marginBottom: 2, color: 'primary.main' }}
          >
            Please try refreshing the page or contact support if the issue persists.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '8px 16px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Refresh Page
            </button>
            <button
              onClick={() => {
                setRetryCount(0);
                setError(false);
                setErrorType(null);
                setLoadingState(true);
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Try Again
            </button>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default PureIframe;
